import React from 'react';
import { KeenIcon } from '@/components';
import { CardRole } from '@/partials/cards';
import { useSettings } from '@/providers';
import { useEffect, useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import settingService from '@/services/setting/settingService';
import { toast } from 'sonner';
import { useAbility } from '@/providers';
import { sortData } from '@/utils';
import { describeRoleSaveError, selectAllPermissions } from '@/pages/account/members/roles/roleForm';

const validationSchema = Yup.object({
  name: Yup.string().required("Role name is required"),
  permissions: Yup.array()
    .of(Yup.object().shape({ name: Yup.string().required() }))
    .test(
      "admin-or-valid-permissions",
      "Select at least one permission or Administrator access",
      (value) => {
        if (!value || value.length === 0) return false;

        const hasIsAdmin = value.some(p => p.name === "is_admin_access");
        const otherPermissions = value.filter(p => p.name !== "is_admin_access");

        return hasIsAdmin && otherPermissions.length === 0 || otherPermissions.length > 0;
      }
    )
    .test(
      "require-view-permissions-for-contact",
      "Please select either 'View All Contacts' or 'View Assigned Contacts'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-contact")) {
          return names.includes("view-all-contact") || names.includes("view-assigned-contact");
        }
        return true;
      }
    )
    .test(
      "require-view-permissions-for-matter",
      "Please select either 'View All Matters' or 'View Assigned Matters'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-matter")) {
          return names.includes("view-all-matter") || names.includes("view-assigned-matter");
        }
        return true;
      }
    )
    .test(
      "require-view-permissions-for-invoice",
      "Please select either 'Read only Invoice' or 'Manage Invoice'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-invoice")) {
          return names.includes("readonly-invoice") || names.includes("manage-invoice");
        }
        return true;
      }
    )
});

const initialValues = {
  name: "",
  active: true,
  permissions: [],
};


const Roles = () => {
  const { getThemeMode } = useSettings();
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [permissionData, setPermissionData] = useState(null)
  const [selectAll, setSelectAll] = useState(false);
  const [rolesData, setRolesData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false)
  const { ability } = useAbility();

  const handleSelectAll = (e, values, setFieldValue) => {
    const { checked } = e.target;
    setFieldValue("permissions", checked ? selectAllPermissions(permissionData) : []);
    setSelectAll(checked);
  };

  // collect all related permission names for a parent
  const collectRelatedNames = (perm) => {
    const names = [perm.name];
    if (perm.options?.length) names.push(...perm.options.map(o => o.name));
    if (perm.children?.length) names.push(...perm.children.map(c => c.name));
    return names;
  };

  // remove an array of names from the current selection
  const removePermissionsByNames = (selected, namesToRemove) => {
    const toRemove = new Set(namesToRemove);
    return selected.filter(p => !toRemove.has(p.name));
  };


  const handleSubmitRoles = async (values, resetForm, setErrors) => {
    const isAdminSelected = values.permissions.some(p => p.name === 'is_admin_access');
    const updatedValues = {
      ...values,
      name: values.name.replace(/\s+/g, '-'),
      permissions: isAdminSelected
        ? ['is_admin_access']
        : values.permissions.map(permission => permission.name),
    };
    setButtonLoading(true);
    try {
      const { status, data } = await settingService.createRole(updatedValues)
      if (status) {
        setShowRoleModal(false);
        getAllRoles()
        toast.success('Role Created successfully', {
          description: 'Role created successfully.',

        });
      }
    } catch (error) {
      console.error('Error:', error)
      const { fieldErrors, message } = describeRoleSaveError(error, 'An error occurred while creating role. Please try again later.');
      setErrors(fieldErrors);
      toast.error('Failed to create role.', { description: message });
    } finally {
      setButtonLoading(false)
    }
  };

  const handleShowModal = async () => {
    setIsLoading(true)
    setShowRoleModal(true);
    try {
      const { data, status } = await settingService.getPermisionsGroup()

      const filtered = Object.fromEntries(
        Object.entries(data).filter(([key]) => key !== "referralAttorney")
      );

      if (status) {
        setPermissionData(filtered)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getAllRoles = async () => {
    setDataLoading(true)
    // setShowRoleModal(true);
    try {
      const { data, status } = await settingService.getAllRolesList()
      if (status) {
        const updatedData = sortData(data, { mode: 'reverse' }).map(obj => ({
          ...obj,
          name: obj.name.replace(/-/g, ' ')
        }));
        setRolesData(updatedData)
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error('Failed to fetch roles.', {
        description: 'An error occurred while fetching roles. Please try again later.',

      });
    } finally {
      setDataLoading(false)
    }
  }

  const renderItem = (item, index) => {
    return <CardRole key={index} getAllRoles={getAllRoles} title={item.name} subTitle={item.active} description={item.description} team={item.team} itemId={item.id} badge={item.badge} />;
  };

  const preprocessPermissions = (permissions) => {
    return permissions.map((perm) => {
      // If this is "invoice", inject "manage-invoice" into options
      if (perm.name === "access-invoice") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-invoice");
        if (!hasManageInOptions) {
          // Find and remove "manage-invoice" from children
          const manageChild = perm.children?.find(child => child.name === "manage-invoice");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-invoice");
          }
        }
      }
      if (perm.name === "access-mass-mailing") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-mass-mailing");
        if (!hasManageInOptions) {
          // Find and remove "manage-mass-mailing" from children
          const manageChild = perm.children?.find(child => child.name === "manage-mass-mailing");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-mass-mailing");
          }
        }
      }
      if (perm.name === "access-intake-form") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-intake-form");
        if (!hasManageInOptions) {
          // Find and remove "manage-intake-form" from children
          const manageChild = perm.children?.find(child => child.name === "manage-intake-form");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-intake-form");
          }
        }
      }
      if (perm.name === "access-document-template") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-document-template");
        if (!hasManageInOptions) {
          // Find and remove "manage-intake-form" from children
          const manageChild = perm.children?.find(child => child.name === "manage-document-template");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-document-template");
          }
        }
      }
      // if (perm.name === "access-reports") {
      //   const hasManageInOptions = perm.options?.some(opt => opt.name === "ar-reports");
      //   if (!hasManageInOptions) {
      //     // Find and remove "ar-reports" from children
      //     const manageChild = perm.children?.find(child => child.name === "ar-reports");
      //     if (manageChild) {
      //       perm.options = [...(perm.options || []), manageChild];
      //       perm.children = perm.children.filter(child => child.name !== "ar-reports");
      //     }
      //   }
      // }
      return perm;
    });
  };


  const renderPermissionGroup = (groupName, permissions, values, setFieldValue) => {
    if (!Array.isArray(permissions)) return null;
    const isAdminSelected = values.permissions.some(
      (perm) => perm.name === "is_admin_access"
    );
    const isChecked = (name) => values.permissions.some(p => p.name === name);
    const isDisabled = (permName) => {
      const selected = values.permissions.map(p => p.name);
      const isAdminOnlySelected = selected.length === 1 && selected[0] === "is_admin_access";
      return isAdminOnlySelected && permName !== "is_admin_access";
    };
    const handleCheck = (checked, name) => {
      let updated = [...values.permissions];
      if (checked) {
        updated.push({ name });
      } else {
        updated = updated.filter(p => p.name !== name);
      }
      setFieldValue("permissions", updated);
    };

    const handleRadio = (groupNames, selectedName) => {
      const updated = values.permissions.filter(p => !groupNames.includes(p.name));
      updated.push({ name: selectedName });
      setFieldValue("permissions", updated);
    };

    return (
      <React.Fragment key={groupName}>
        <tr>
          <td className="!py-5.5 font-bold text-md capitalize">
            {groupName
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
            }
          </td>
          <td colSpan={6}></td>
        </tr>

        {permissions.map((perm) => {
          const isPermChecked = isChecked(perm.name);
          const hasOptions = perm.options?.length > 0;
          const hasChildren = perm.children?.length > 0;

          return (
            <React.Fragment key={perm.name}>
              {/* Parent Checkbox */}
              <tr>
                <td className="pl-8 text-sm font-medium text-gray-700">{perm.label}</td>
                <td colSpan={6} className="text-right">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-input"
                    checked={isPermChecked}
                    value={perm.name}
                    disabled={isDisabled(perm.name)}
                    // onChange={(e) => handleCheck(e.target.checked, perm.name)}
                    onChange={(e) => {
                      const { checked } = e.target;
                      let updated = [...values.permissions];

                      if (checked) {
                        // add only the parent if not already present
                        if (!isPermChecked) updated.push({ name: perm.name });
                      } else {
                        // parent unchecked ⇒ remove parent + all its options + all its children
                        const allNames = collectRelatedNames(perm);
                        updated = removePermissionsByNames(updated, allNames);
                      }

                      setFieldValue("permissions", updated);
                    }}
                  />
                </td>
              </tr>

              {/* Radio options (if parent is checked and options exist) */}
              {isPermChecked && hasOptions && (
                <tr>
                  <td className="pl-12 text-sm text-gray-600">Options</td>
                  <td colSpan={6} className="text-right">
                    {perm.options.map((opt) => {
                      const isOptChecked = isChecked(opt.name);

                      return (
                        <label key={opt.name} className="flex items-center justify-end gap-2 mb-1.5">
                          <span className="ml-1">{opt.label}</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-input"
                            checked={isPermChecked ? isOptChecked : false}
                            value={opt.name}
                            disabled={isDisabled(opt.name)}
                            onChange={() => {
                              // Ensure only one of the options can be selected at a time
                              const updated = values.permissions.filter(
                                (p) => !perm.options.map(o => o.name).includes(p.name)
                              );

                              // Add the newly selected one (if not already checked)
                              if (!isOptChecked) {
                                updated.push({ name: opt.name });
                              }

                              setFieldValue("permissions", updated);
                            }}
                          />
                        </label>
                      );
                    })}
                  </td>
                </tr>
              )}

              {hasChildren &&
                perm.children.map((child) => {
                  let showChild = false;

                  if (!hasOptions) {
                    if (perm.name === "access-reports") {
                      // show children ONLY when parent is checked
                      showChild = isPermChecked;
                    } else {
                      // keep old behavior for every other module without options
                      showChild = true;
                    }
                  } else {
                    // Show children for invoice if manage-invoice is selected
                    if (perm.name === "access-invoice") {
                      showChild = isChecked("manage-invoice");
                    }

                    else if (perm.name === "access-mass-mailing") {
                      showChild = isChecked("manage-mass-mailing");
                    }
                    // Show children for contact and matter if any view-* option is selected
                    else if (["access-contact", "access-matter"].includes(perm.name)) {
                      showChild = perm.options.some(opt => opt.name.startsWith("view-") && isChecked(opt.name));
                    }

                    // For other permissions, show children if any option is checked
                    else {
                      showChild = perm.options.some(opt => isChecked(opt.name));
                    }
                  }

                  return showChild ? (
                    <tr key={child.name}>
                      <td className="pl-16 text-sm text-gray-600">{child.label}</td>
                      <td colSpan={6} className="text-right">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm checkbox-input"
                          value={child.name}
                          disabled={isDisabled(child.name)}
                          checked={isChecked(child.name)}
                          onChange={(e) => handleCheck(e.target.checked, child.name)}
                        />
                      </td>
                    </tr>
                  ) : null;
                })}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };





  useEffect(() => {
    getAllRoles()
  }, [])

  return (
    <>
      <div className={`grid grid-cols-1 lg:grid-cols-1 gap-5 lg:gap-7.5 mb-4`}>
        <CardRole
          data={rolesData}
          dataLoading={dataLoading}
          getAllRoles={getAllRoles}
          openRoleModal={handleShowModal}
        />

        {showRoleModal && (
          <div className="modal-wrapper modal-animate-fade">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm, setErrors }) => {
                handleSubmitRoles(values, resetForm, setErrors);
              }}
            >
              {({ setFieldValue, values, errors, setErrors }) => (
                console.log(errors),
                <Form className="modal-animate-scale modal-body w-4/5 max-w-3xl">
                  <button
                    type="button"
                    onClick={() => setShowRoleModal(false)}
                    className="modal-close-btn"
                  >
                    <KeenIcon icon="cross" />
                  </button>

                  <h2 className="mb-5 text-lg font-medium text-center text-gray-900">
                    Create New Role
                  </h2>

                  {/* Role Name */}
                  <div className="mb-4">
                    <label className="mb-1 text-gray-900 form-label">Role Name</label>
                    <Field
                      type="text"
                      name="name"
                      placeholder="Enter Role Name"
                      className="input"
                    />
                    <ErrorMessage name="name" component="div" className="mt-1 text-xs text-red-500" />
                  </div>

                  <div className='flex flex-wrap items-center justify-between'>
                    <div className="flex items-center mb-4">
                      <label htmlFor="selectAll" className="mr-2">Select All</label>
                      <input
                        type="checkbox"
                        id="selectAll"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e, values, setFieldValue)}
                        className="cursor-pointer checkbox checkbox-sm"
                      />
                    </div>

                    {/* Active Checkbox */}
                    <div className="mb-4">
                      <Field name="active">
                        {({ field, form }) => (
                          <label className="switch w-fit">
                            <span className="switch-label">Active</span>
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={() => form.setFieldValue("active", !field.value)}
                            />
                          </label>
                        )}
                      </Field>
                    </div>

                  </div>

                  {/* Permissions (Example with checkboxes) */}
                  <div className="card">
                    <div className="card-table scrollable-x-auto max-h-[400px]">
                      <table className="table">
                        <thead>
                          <tr>
                            <th className="text-start text-gray-300 font-normal min-w-[300px]">Module</th>
                            <th className="font-normal text-center text-gray-700 min-w-24"></th>
                          </tr>
                        </thead>
                        <tbody className="font-medium text-gray-900">
                          {isLoading ? (
                            <tr>
                              <td colSpan="5" className="py-5 text-center">Loading...</td>
                            </tr>
                          ) : permissionData?.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="py-5 text-center">No data available</td>
                            </tr>
                          ) : (
                            Object.entries(permissionData).map(([groupName, permissions]) =>
                              renderPermissionGroup(groupName, preprocessPermissions(permissions), values, setFieldValue)
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <ErrorMessage name="permissions" component="div" className="mt-1 text-xs text-red-500" />

                  <button
                    type="submit"
                    disabled={buttonLoading}
                    className="flex justify-center w-full mt-4 btn btn-primary grow"
                  >
                    {buttonLoading ? "Please Wait..." : "Submit"}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </>
  )
};
export { Roles };